/**
 * Shared utilities, constants, and sub-components for Lab Reports.
 * Used by both LabReportsSection (patient record page) and LabReportsModal (consultation page).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Image, Loader2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { createComponentLogger } from '@/lib/utils/logger';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/base/Modal';

import { useUploadLabReport, useDeleteLabReport } from '@/lib/hooks/useLabReports';
import { fetchBinaryAsBlob, getCachedBlobUrl } from '@/lib/api/labReports';
import type { LabReportRecord } from '@/lib/api/labReports';

const logger = createComponentLogger('lab-reports-shared');

// ─── Constants ───────────────────────────────────────────────────────────────

export const ACCEPTED_MIME = [
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/webp',
  'image/bmp',
  'application/pdf',
];
export const ACCEPTED_EXT = '.jpg,.jpeg,.png,.tiff,.tif,.webp,.bmp,.pdf';
export const MAX_FILES = 20;
export const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function validateFile(file: File): string | null {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return `"${file.name}" is not a supported file type.`;
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `"${file.name}" exceeds the 20 MB size limit.`;
  }
  return null;
}

export function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function FileIcon({ contentType }: { contentType: string }) {
  if (contentType.startsWith('image/')) {
    return <Image className="h-4 w-4 text-[#4B6EE7]" />;
  }
  return <FileText className="h-4 w-4 text-[#4B6EE7]" />;
}

// ─── Shared Hook: upload/delete/input logic with staging ─────────────────────

interface UseLabReportActionsOptions {
  patientId?: string;
  encounterId?: string;
  appointmentId?: string;
}

export interface StagedFile {
  file: File;
  previewUrl: string;
}

export function useLabReportActions({
  patientId,
  encounterId,
  appointmentId,
}: UseLabReportActionsOptions) {
  const { mutateAsync: upload, isPending: isUploading } = useUploadLabReport(
    patientId,
    encounterId,
    appointmentId
  );
  const { mutateAsync: deleteReport, isPending: isDeleting } = useDeleteLabReport(patientId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const stagedFilesRef = useRef(stagedFiles);
  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);

  // Revoke all blob URLs on unmount
  useEffect(() => {
    return () => {
      stagedFilesRef.current.forEach((sf) => URL.revokeObjectURL(sf.previewUrl));
    };
  }, []);

  const stageFiles = useCallback(
    (files: FileList | File[]) => {
      if (!patientId) {
        toast.error('No patient selected');
        return;
      }
      const list = Array.from(files).slice(0, MAX_FILES);
      for (const file of list) {
        const err = validateFile(file);
        if (err) {
          toast.error(err);
          return;
        }
      }
      const validated = list.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
      setStagedFiles((prev) => [...prev, ...validated].slice(0, MAX_FILES));
    },
    [patientId]
  );

  const removeStagedFile = useCallback((index: number) => {
    setStagedFiles((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearStagedFiles = useCallback(() => {
    setStagedFiles((prev) => {
      prev.forEach((sf) => URL.revokeObjectURL(sf.previewUrl));
      return [];
    });
  }, []);

  const uploadStagedFiles = useCallback(async (): Promise<boolean> => {
    if (!patientId) {
      toast.error('No patient selected');
      return false;
    }
    const failed: StagedFile[] = [];
    const toUpload = [...stagedFiles];
    logger.log(
      '[uploadStagedFiles] Starting upload of',
      toUpload.length,
      'file(s) | patientId:',
      patientId
    );
    for (const sf of toUpload) {
      try {
        logger.log('[uploadStagedFiles] Uploading:', sf.file.name, '| size:', sf.file.size);
        await upload(sf.file);
        logger.log('[uploadStagedFiles] Upload SUCCESS:', sf.file.name);
        toast.success(`"${sf.file.name}" uploaded successfully`);
        URL.revokeObjectURL(sf.previewUrl);
      } catch (err) {
        logger.error('[uploadStagedFiles] Upload FAILED:', sf.file.name, err);
        toast.error(`Failed to upload "${sf.file.name}"`);
        failed.push(sf);
      }
    }
    setStagedFiles(failed);
    return failed.length === 0;
  }, [patientId, stagedFiles, upload]);

  const previewStagedFile = useCallback((staged: StagedFile) => {
    window.open(staged.previewUrl, '_blank', 'noopener');
  }, []);

  /**
   * Lazily resolve a blob URL for a report. Returns the cached URL instantly
   * if available, otherwise fetches the Binary content and caches it.
   */
  const loadReportUrl = useCallback(
    async (report: LabReportRecord): Promise<string | undefined> => {
      // Already have a blob URL (from cache or upload)
      const cached = getCachedBlobUrl(report.id);
      if (cached) return cached;
      if (report.url) return report.url;

      // Need to fetch the binary content
      if (!report.binaryRef?.startsWith('Binary/')) return undefined;
      const binaryId = report.binaryRef.split('/')[1];
      try {
        return await fetchBinaryAsBlob(binaryId, report.id, report.contentType);
      } catch {
        toast.error(`Failed to load "${report.fileName}"`);
        return undefined;
      }
    },
    []
  );

  const handleDelete = useCallback(
    async (report: LabReportRecord | null, onDone: () => void) => {
      if (!report) return;
      try {
        await deleteReport(report.id);
        toast.success(`"${report.fileName}" deleted`);
      } catch {
        toast.error(`Failed to delete "${report.fileName}"`);
      } finally {
        onDone();
      }
    },
    [deleteReport]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        stageFiles(e.target.files);
        e.target.value = '';
      }
    },
    [stageFiles]
  );

  return {
    inputRef,
    isUploading,
    isDeleting,
    stagedFiles,
    stageFiles,
    removeStagedFile,
    clearStagedFiles,
    uploadStagedFiles,
    previewStagedFile,
    loadReportUrl,
    handleDelete,
    onInputChange,
  };
}

// ─── Staged Files List Component ─────────────────────────────────────────────

interface StagedFilesListProps {
  stagedFiles: StagedFile[];
  isUploading: boolean;
  onPreview: (staged: StagedFile) => void;
  onRemove: (index: number) => void;
  onCancel: () => void;
  onUploadAll: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function StagedFilesList({
  stagedFiles,
  isUploading,
  onPreview,
  onRemove,
  onCancel,
  onUploadAll,
}: StagedFilesListProps) {
  if (stagedFiles.length === 0) return null;

  return (
    <div className="px-6 py-4">
      <p className="mb-2 font-hanken text-sm font-medium text-[#253057]">
        {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} selected
      </p>
      <div className="flex max-h-[200px] flex-col gap-2 overflow-y-auto">
        {stagedFiles.map((staged, index) => (
          <div
            key={`${staged.file.name}-${index}`}
            className="flex items-center gap-3 rounded-lg border border-[#EAEBF3] bg-white px-3 py-2"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF2FF]">
              <FileIcon contentType={staged.file.type} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-hanken text-sm font-medium text-[#253057]">
                {staged.file.name}
              </p>
              <p className="font-hanken text-xs text-[#8D90A1]">
                {formatFileSize(staged.file.size)}
              </p>
            </div>
            <span className="flex-shrink-0 rounded bg-[#EEF2FF] px-1.5 py-0.5 font-hanken text-[10px] font-semibold uppercase tracking-wide text-[#4B6EE7]">
              {staged.file.name.split('.').pop()?.toUpperCase() || 'FILE'}
            </span>
            <button
              type="button"
              title="Preview in new tab"
              onClick={() => onPreview(staged)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8D90A1] transition-colors hover:bg-[#EEF2FF] hover:text-[#061A61]"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Remove"
              onClick={() => onRemove(index)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8D90A1] transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isUploading}
          className="rounded-lg border border-[#EAEBF3] px-4 py-1.5 font-hanken text-sm font-medium text-[#586695] transition-colors hover:bg-[#F5F6F9] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onUploadAll}
          disabled={isUploading}
          className="flex items-center gap-1.5 rounded-lg bg-[#061A61] px-4 py-1.5 font-hanken text-sm font-medium text-white transition-colors hover:bg-[#0A2580] disabled:opacity-50"
        >
          {isUploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Upload All
        </button>
      </div>
    </div>
  );
}

// ─── View Report Helper ──────────────────────────────────────────────────────

/**
 * Opens a report for viewing. PDFs open directly in a new browser tab
 * (Chrome Incognito blocks embedded PDF rendering of blob URLs).
 * Non-PDFs are passed to the provided callback to open in the preview dialog.
 */
export function viewReport(
  report: LabReportRecord,
  setPreview: (r: LabReportRecord) => void
): void {
  if (!report.url) return;
  if (report.contentType === 'application/pdf') {
    window.open(report.url, '_blank', 'noopener');
  } else {
    setPreview(report);
  }
}

// ─── Report Preview Dialog ───────────────────────────────────────────────────

interface ReportPreviewDialogProps {
  report: LabReportRecord | null;
  onClose: () => void;
}

export function ReportPreviewDialog({ report, onClose }: ReportPreviewDialogProps) {
  return (
    <Dialog open={!!report} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="xl" hideCloseButton className="overflow-hidden rounded-2xl p-0">
        {report && (
          <>
            <DialogHeader className="flex-row items-center justify-between border-b border-[#EAEBF3] px-5 py-3 sm:text-left">
              <div className="flex min-w-0 items-center gap-2">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#EEF2FF]">
                  <FileIcon contentType={report.contentType} />
                </div>
                <DialogTitle
                  className="truncate font-hanken text-sm font-medium text-[#253057]"
                  title={report.fileName}
                >
                  {report.fileName}
                </DialogTitle>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {report.url && (
                  <a
                    href={report.url}
                    download={report.fileName}
                    className="flex items-center gap-1.5 rounded-lg border border-[#EAEBF3] px-3 py-1.5 font-hanken text-sm font-medium text-[#585859] transition-colors hover:bg-[#F5F6F9]"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                )}
                <DialogClose className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8D90A1] transition-colors hover:bg-[#F5F6F9] hover:text-[#42434B]">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close preview</span>
                </DialogClose>
              </div>
            </DialogHeader>

            <div className="flex h-[68vh] items-center justify-center bg-[#F5F6F9] p-4">
              {report.contentType.startsWith('image/') ? (
                <img
                  src={report.url}
                  alt={report.fileName}
                  className="max-h-full max-w-full rounded-lg object-contain shadow"
                />
              ) : (
                <div className="text-center">
                  <FileText className="mx-auto mb-3 h-12 w-12 text-[#8D90A1]" />
                  <p className="font-hanken text-sm text-[#585859]">
                    Preview not available for this file type.
                  </p>
                  {report.url && (
                    <a
                      href={report.url}
                      download={report.fileName}
                      className="mt-3 inline-flex items-center gap-1.5 font-hanken text-sm font-medium text-[#4B6EE7] hover:underline"
                    >
                      <Eye className="h-4 w-4" />
                      Open in new tab
                    </a>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Confirmation Dialog ──────────────────────────────────────────────

interface DeleteConfirmDialogProps {
  report: LabReportRecord | null;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function DeleteConfirmDialog({
  report,
  isDeleting,
  onClose,
  onConfirm,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog open={!!report} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="sm" hideCloseButton className="overflow-hidden rounded-2xl p-0">
        <div className="px-6 py-5">
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
          <DialogTitle className="mb-1 font-serif text-lg font-normal text-[#051858]">
            Delete Report
          </DialogTitle>
          <p className="font-hanken text-sm text-[#586695]">
            Are you sure you want to delete{' '}
            <span className="font-medium text-[#253057]">{report?.fileName}</span>? This action
            cannot be undone.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[#EAEBF3] px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-[#EAEBF3] px-4 py-1.5 font-hanken text-sm font-medium text-[#586695] transition-colors hover:bg-[#F5F6F9] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-1.5 font-hanken text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Delete
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
